/**
 * VTT Processing Utilities
 * Handles WebVTT file manipulation for video editing operations
 */

export interface VTTCue {
  startTime: number; // in seconds
  endTime: number; // in seconds
  text: string;
}

export class VTTProcessor {
  /**
   * Parse VTT content into cues
   */
  static parseVTT(vttContent: string): VTTCue[] {
    const cues: VTTCue[] = [];
    const lines = vttContent.split('\n');
    
    let i = 0;
    // Skip header
    while (i < lines.length && !lines[i].includes('-->')) {
      i++;
    }
    
    while (i < lines.length) {
      const line = lines[i].trim();
      
      if (line.includes('-->')) {
        const [startStr, endStr] = line.split('-->').map(s => s.trim());
        const startTime = this.parseTimestamp(startStr);
        const endTime = this.parseTimestamp(endStr);
        
        // Get the text (can be multiple lines)
        const textLines: string[] = [];
        i++;
        while (i < lines.length && lines[i].trim() !== '') {
          textLines.push(lines[i]);
          i++;
        }
        
        if (textLines.length > 0) {
          cues.push({
            startTime,
            endTime,
            text: textLines.join('\n')
          });
        }
      }
      i++;
    }
    
    return cues;
  }
  
  /**
   * Parse timestamp string to seconds
   * Format: HH:MM:SS.mmm or MM:SS.mmm
   */
  static parseTimestamp(timestamp: string): number {
    const parts = timestamp.split(':');
    let seconds = 0;
    
    if (parts.length === 3) {
      // HH:MM:SS.mmm
      seconds = parseInt(parts[0]) * 3600 + parseInt(parts[1]) * 60 + parseFloat(parts[2]);
    } else if (parts.length === 2) {
      // MM:SS.mmm
      seconds = parseInt(parts[0]) * 60 + parseFloat(parts[1]);
    } else {
      // SS.mmm
      seconds = parseFloat(parts[0]);
    }
    
    return seconds;
  }
  
  /**
   * Format seconds to VTT timestamp
   */
  static formatTimestamp(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    const pad = (n: number) => n.toString().padStart(2, '0');
    const formatSecs = (s: number) => s.toFixed(3).padStart(6, '0');
    
    if (hours > 0) {
      return `${pad(hours)}:${pad(minutes)}:${formatSecs(secs)}`;
    } else {
      return `${pad(minutes)}:${formatSecs(secs)}`;
    }
  }
  
  /**
   * Adjust VTT cues for a trimmed video
   * @param vttContent Original VTT content
   * @param trimStart Start time of trim in seconds
   * @param trimEnd End time of trim in seconds
   * @returns Adjusted VTT content
   */
  static adjustVTTForTrim(vttContent: string, trimStart: number, trimEnd: number): string {
    const cues = this.parseVTT(vttContent);
    const adjustedCues: VTTCue[] = [];
    
    for (const cue of cues) {
      // Skip cues that are completely outside the trim range
      if (cue.endTime < trimStart || cue.startTime > trimEnd) {
        continue;
      }
      
      // Adjust cue timing
      let adjustedStart = Math.max(0, cue.startTime - trimStart);
      let adjustedEnd = cue.endTime - trimStart;
      
      // Clip to trim bounds
      if (cue.startTime < trimStart) {
        adjustedStart = 0;
      }
      if (cue.endTime > trimEnd) {
        adjustedEnd = trimEnd - trimStart;
      }
      
      adjustedCues.push({
        startTime: adjustedStart,
        endTime: adjustedEnd,
        text: cue.text
      });
    }
    
    // Generate new VTT content
    return this.generateVTT(adjustedCues);
  }
  
  /**
   * Generate VTT content from cues
   */
  static generateVTT(cues: VTTCue[]): string {
    const lines = ['WEBVTT', ''];
    
    for (let i = 0; i < cues.length; i++) {
      const cue = cues[i];
      lines.push(`${i + 1}`);
      lines.push(`${this.formatTimestamp(cue.startTime)} --> ${this.formatTimestamp(cue.endTime)}`);
      lines.push(cue.text);
      lines.push('');
    }
    
    return lines.join('\n');
  }
  
  /**
   * Fetch and adjust VTT from URL
   */
  static async fetchAndAdjustVTT(vttUrl: string, trimStart: number, trimEnd: number): Promise<string> {
    try {
      const response = await fetch(vttUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch VTT: ${response.statusText}`);
      }

      const vttContent = await response.text();
      return this.adjustVTTForTrim(vttContent, trimStart, trimEnd);
    } catch (error) {
      console.error('[VTTProcessor] Error fetching/adjusting VTT:', error);
      throw error;
    }
  }

  /**
   * Replace text in VTT captions (for visual edits like typo fixes, branding)
   * @param vttContent Original VTT content
   * @param replacements Array of find/replace pairs
   * @returns Modified VTT content
   */
  static modifyVTTText(
    vttContent: string,
    replacements: Array<{findText: string, replaceWith: string}>
  ): string {
    const cues = this.parseVTT(vttContent);

    const modifiedCues = cues.map(cue => {
      let modifiedText = cue.text;

      for (const repl of replacements) {
        modifiedText = modifiedText.replace(
          new RegExp(`\\b${this.escapeRegex(repl.findText)}\\b`, 'gi'),
          repl.replaceWith
        );
      }

      return {
        ...cue,
        text: modifiedText
      };
    });

    return this.generateVTT(modifiedCues);
  }

  /**
   * Remove specific words from VTT by hiding their cues
   * @param vttContent Original VTT content
   * @param wordsToRemove Array of word timestamps to remove
   * @returns Modified VTT with specified cues removed
   */
  static removeWordsFromVTT(
    vttContent: string,
    wordsToRemove: Array<{text: string, startTime: number, endTime: number}>
  ): string {
    const cues = this.parseVTT(vttContent);

    const filteredCues = cues.filter(cue => {
      for (const word of wordsToRemove) {
        const wordStart = word.startTime / 1000;
        const wordEnd = word.endTime / 1000;

        const cueOverlapsWord =
          (cue.startTime <= wordStart && cue.endTime >= wordStart) ||
          (cue.startTime <= wordEnd && cue.endTime >= wordEnd) ||
          (cue.startTime >= wordStart && cue.endTime <= wordEnd);

        if (cueOverlapsWord && cue.text.toLowerCase().includes(word.text.toLowerCase())) {
          return false;
        }
      }
      return true;
    });

    return this.generateVTT(filteredCues);
  }

  /**
   * Escape special regex characters
   */
  private static escapeRegex(str: string): string {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /**
   * Fetch VTT from URL
   */
  static async fetchVTT(vttUrl: string): Promise<string> {
    try {
      const response = await fetch(vttUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch VTT: ${response.statusText}`);
      }
      return await response.text();
    } catch (error) {
      console.error('[VTTProcessor] Error fetching VTT:', error);
      throw error;
    }
  }

  /**
   * Generate word-level chunked VTT from word data
   * Creates captions with 3 words each for better readability
   */
  static generateChunkedVTT(
    words: Array<{text: string; start: number; end: number}>,
    wordsPerChunk: number = 3
  ): string {
    let vtt = 'WEBVTT\n\n';

    for (let i = 0; i < words.length; i += wordsPerChunk) {
      const chunk = words.slice(i, Math.min(i + wordsPerChunk, words.length));

      if (chunk.length === 0) continue;

      const text = chunk.map(w => w.text).join(' ');
      const startMs = chunk[0].start;
      const endMs = chunk[chunk.length - 1].end;

      const cueNumber = Math.floor(i / wordsPerChunk) + 1;

      vtt += `${cueNumber}\n`;
      vtt += `${this.formatTimestamp(startMs / 1000)} --> ${this.formatTimestamp(endMs / 1000)}\n`;
      vtt += `${text}\n\n`;
    }

    return vtt;
  }
}